import { Component, OnInit, Inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import "chartjs-adapter-moment";
import "chartjs-plugin-annotation";
import { Video } from "../../../models/video";
import { ApiService } from "../../../services/api/api.service";
import {
  ChartPerformancePeriod,
  DateRangeOptions,
  ModuleType,
} from "src/app/models/enums";
import { VideoCreativeStats } from "src/app/models/videosCreativeStats";
import { StateService } from "src/app/services/shared/state.service";
import { PopupService } from "src/app/services/shared/popup.service";
import { ActivatedRoute, NavigationExtras, Router } from "@angular/router";
import { StaticDataService } from "src/app/services/shared/static.data.service";

@Component({
  selector: "app-video-detail",
  templateUrl: "./video-detail.component.html",
  styleUrls: ["./video-detail.component.scss"],
})
export class VideoDetailComponent implements OnInit {
  ytVideoId: string = "";
  swipeBoardIds: number[] | undefined = [];
  yValues: string[] = [];
  xValues: number[] = [];

  video: Video = new Video();
  videoStats: VideoCreativeStats = new VideoCreativeStats();
  showTextChecked: boolean = false;
  isPayingUser: boolean = false;

  chartPeriod: string = ChartPerformancePeriod.Year;
  selectedRangeIndex: number = DateRangeOptions.Year;
  daysBackwardsArray: number[] = [-30, -365, 0];
  periodMap: Map<string, number> = new Map<string, number>([
    [ChartPerformancePeriod.Month, -30],
    [ChartPerformancePeriod.Year, -365],
    [ChartPerformancePeriod.Total, 0],
  ]);

  shareUrl: string = "";

  constructor(
    public apiService: ApiService,
    public stateService: StateService,
    public router: Router,
    private popupService: PopupService,
    public staticDataService: StaticDataService,
    public activatedRoute: ActivatedRoute,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<VideoDetailComponent>
  ) {
    this.ytVideoId = data.ytVideoId;
    this.CreateShareUrl();
  }

  async ngOnInit() {
    this.SubscribeToUser();
    Promise.all([this.LoadVideoInfo(), this.LoadVideoStats()]);
  }

  private async LoadVideoInfo() {
    const result = await this.apiService.GetVideoInfo(this.ytVideoId, "", "");

    if (result?.errorMessage) {
      this.popupService.Notify(result.errorMessage);
      return;
    }

    if (result?.data) {
      this.video = result.data;
    }
  }

  private async LoadVideoStats() {
    const result = await this.apiService.GetVideoStatistics(
      this.ytVideoId,
      "",
      ""
    );

    if (result?.errorMessage) {
      this.popupService.Notify(result.errorMessage);
      return;
    }

    if (result?.data) {
      this.videoStats = result.data;
    }
  }

  public SubscribeToUser() {
    this.stateService.user$.subscribe((user) => {
      this.isPayingUser = this.stateService.IsPayingUserState(user);
    });
  }

  public Close() {
    this.dialogRef.close();
  }

  public get getCurrentUrl() {
    return (
      window.location.origin + "/#/dashboard/ad-info?videoId=" + this.ytVideoId
    );
  }

  public getBlurIfNeeded() {
    return {
      filter: !this.isPayingUser ? "blur(7px)" : "blur(0)",
      "user-select": !this.isPayingUser ? "none" : "auto",
      "pointer-events": !this.isPayingUser ? "none" : "auto",
    };
  }

  public OpenAdInfoPage() {
    let isAffiliate =
      this.stateService.moduleType == ModuleType.Affiliate ? true : false;
    this.Close();
    const queryParams: NavigationExtras = {
      queryParams: {
        videoId: this.video.ytVideoId,
        ...(isAffiliate && { isAffiliate }),
      },
      //replaceUrl: true,
      relativeTo: this.activatedRoute,
    };

    this.router.navigate(["dashboard/ad-info"], queryParams);
  }

  public OpenVideoBrand() {
    this.Close();
    this.popupService.OpenBrandPreview(this.video.brandId);
  }

  public async OpenVideoCategory() {
    this.Close();
    this.stateService.ModuleSelected(ModuleType.Marketer); // if in affiliate mode to switch to marketer before opening ranking page
    await this.router.navigate(["/dashboard/rankings"], {
      queryParams: { selectedCategoryId: this.video.categoryId },
    });
  }

  public OpenInNewTab(url: string) {
    window.open(url, "_blank");
  }

  public async SwipeSaved(boardsIds?: number[]) {
    this.swipeBoardIds = boardsIds;
    const result = await this.apiService.SwipeVideo(
      this.ytVideoId,
      this.swipeBoardIds
    );
    if (result?.errorMessage) {
      this.popupService.Notify(result?.errorMessage);
      return;
    }
    this.video.swiped = true;
    this.popupService.Notify("Video successfuly swiped.");
  }
  public async RemoveSwipe() {
    this.popupService.AskToConfirm(
      "Swipe deletion",
      "Are you sure that you want to delete selected swipe?",
      async () => await this.SwipeRemoved()
    );
  }

  public async SwipeRemoved() {
    const result = await this.apiService.RemoveSwipedVideo(this.ytVideoId);

    if (result?.errorMessage) {
      this.popupService.Notify(result?.errorMessage);
      return;
    }

    this.video.swiped = false;
    this.popupService.Notify("Video successfuly removed from Swipes.");
    this.dialogRef.close("deleted");
  }

  public get ShareUrl() {
    return this.shareUrl;
  }

  public async CreateShareUrl() {
    this.shareUrl = await this.GetShareUrl({
      ytVideoId: this.ytVideoId,
    });
  }

  public async GetShareUrl(data: object) {
    const key = await this.CreateKey(data);
    return window.location.origin + "/#/shared/" + key + "/ad-info";
  }

  private async CreateKey(data: object) {
    const result = await this.apiService.GetKey(data);
    if (result?.errorMessage) this.popupService.Notify(result.errorMessage);
    if (result?.data) return result?.data;
  }
}
